import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { EmptyState } from '@lyearn/molecules';
import * as ContentListModal from '@/components/ContentListModal';
import Loader from '@/components/Loader';
import useUsers from '@/components/UsersSearch/hooks/useUsers';
import useDebounceState from '@/hooks/useDebounceState';
import useEntitySet from '@/hooks/useEntitySet';

import UserCard from './UserCard';

function AddInstructorDialog({
  selectedUsers = [],
  onClose,
  onSubmit,
}: {
  selectedUsers?: string[];
  onClose: () => void;
  onSubmit: (ids: string[]) => void;
}) {
  const { t } = useTranslation();
  const { selectedItemsSet, toggle } = useEntitySet(selectedUsers);
  const [searchInput, setSearchInput, searchString] = useDebounceState('');
  const {
    data: users,
    isFetching,
    fetchMore,
  } = useUsers({
    searchText: searchString,
    crossSubWorkspace: true,
  });

  const _onSubmit = useCallback(() => {
    onSubmit(Array.from(selectedItemsSet));
    onClose();
  }, [onClose, onSubmit, selectedItemsSet]);

  const renderContent = () => {
    if (isFetching) {
      return <Loader />;
    }

    if (!users?.length) {
      return (
        <EmptyState
          className="flex-1"
          description={t('Common:EmptyState.NoResultsSearchDescription')}
          title={t('Common:EmptyState.NoResultsSearch', { searchText: searchInput })}
        />
      );
    }

    if (users?.length) {
      return (
        <ContentListModal.Body
          className="flex-1"
          itemRenderer={(index: number) => {
            const currentUser = users?.[index];
            if (!currentUser) {
              return null;
            }

            const isSelected = selectedItemsSet.has(currentUser.id);

            return (
              <UserCard
                key={currentUser.id}
                className="mb-4 border-b border-divider pt-12 pb-16 ps-16 pe-20"
                isSelected={isSelected}
                user={currentUser}
                onAction={{ onSelect: toggle }}
              />
            );
          }}
        />
      );
    }

    return null;
  };

  return (
    <ContentListModal.Main
      action={{
        text: t('Events:Editor.AddInstructors'),
        onSubmit: _onSubmit,
      }}
      fetchMore={fetchMore}
      header={{
        title: t('Events:Editor.AddInstructors'),
        description: t('Events:Editor.AddInstructorsDescription'),
      }}
      items={users}
      searchBar={{
        selectedCount: selectedItemsSet.size,
        value: searchInput,
        onChange: setSearchInput,
      }}
      onClose={onClose}>
      {renderContent()}
    </ContentListModal.Main>
  );
}

export default AddInstructorDialog;
